import React, { useState, useRef, useEffect } from 'react';

// Simple custom guided tour component
const GuidedTour = ({ steps, stepIndex, onNext, onPrev, onClose, visible }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: 'bottom' });
  const tooltipRef = useRef(null);
  const step = steps[stepIndex];
  const margin = 16;

  // Helper to update highlight position and tooltip position
  const updateCoords = () => {
    if (!visible || !step) return;
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      // Store both viewport and document coordinates
      const viewportCoords = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };

      const documentCoords = {
        top: rect.top + scrollY,
        left: rect.left + scrollX,
        width: rect.width,
        height: rect.height,
      };

      setCoords({ ...documentCoords, viewport: viewportCoords });

      // Tooltip positioning
      setTimeout(() => {
        const tooltip = tooltipRef.current;
        if (tooltip) {
          const tooltipHeight = tooltip.offsetHeight;
          const tooltipWidth = tooltip.offsetWidth;
          let placement = 'bottom';
          let top = rect.top + scrollY + rect.height + margin;
          // If not enough space below, show above
          if (top + tooltipHeight > window.innerHeight + scrollY) {
            placement = 'top';
            top = rect.top + scrollY - tooltipHeight - margin;
          }
          // Clamp left position
          let left = rect.left + scrollX + rect.width / 2 - tooltipWidth / 2;
          left = Math.max(margin, Math.min(left, window.innerWidth + scrollX - tooltipWidth - margin));
          setTooltipPos({ top, left, placement });
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (!visible || !step) return;
    const el = document.querySelector(step.target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      updateCoords();
    }
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, visible, steps]);

  useEffect(() => {
    updateCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords.top, coords.left, coords.width, coords.height]);

  if (!visible || !step || !coords || !coords.viewport) return null;
  return (
    <>
      {/* Overlay with a transparent hole over the highlighted element */}
      {/* Top overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: Math.max(0, coords.viewport.top - 6),
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      {/* Left overlay */}
      <div
        style={{
          position: 'fixed',
          top: Math.max(0, coords.viewport.top - 6),
          left: 0,
          width: Math.max(0, coords.viewport.left - 6),
          height: coords.viewport.height + 12,
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      {/* Right overlay */}
      <div
        style={{
          position: 'fixed',
          top: Math.max(0, coords.viewport.top - 6),
          left: coords.viewport.left + coords.viewport.width + 6,
          width: Math.max(0, window.innerWidth - (coords.viewport.left + coords.viewport.width + 6)),
          height: coords.viewport.height + 12,
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      {/* Bottom overlay */}
      <div
        style={{
          position: 'fixed',
          top: coords.viewport.top + coords.viewport.height + 6,
          left: 0,
          width: '100vw',
          height: Math.max(0, window.innerHeight - (coords.viewport.top + coords.viewport.height + 6)),
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      {/* Highlighted target border */}
      <div
        style={{
          position: 'fixed',
          top: coords.viewport.top - 6,
          left: coords.viewport.left - 6,
          width: coords.viewport.width + 12,
          height: coords.viewport.height + 12,
          border: '3px solid #000',
          borderRadius: 12,
          boxShadow: '0 0 0 6px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
          zIndex: 10000,
          transition: 'all 0.2s cubic-bezier(.4,2,.6,1)',
        }}
      />
      {/* Tooltip/modal */}
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          top: tooltipPos.top,
          left: tooltipPos.left,
          zIndex: 10001,
          minWidth: 260,
          maxWidth: 320,
        }}
      >
        <div className="bg-white text-black rounded-xl shadow-2xl p-4 border border-gray-200 animate-fade-in">
          <div className="mb-2 font-semibold text-base">Step {stepIndex + 1} of {steps.length}</div>
          <div className="mb-4 text-sm">{step.content}</div>
          <div className="flex justify-between gap-2">
            <button
              className="px-3 py-1 rounded bg-black text-white text-xs font-medium border border-black hover:bg-gray-900"
              onClick={onPrev}
              disabled={stepIndex === 0}
            >Back</button>
            <button
              className="px-3 py-1 rounded bg-white text-black text-xs font-medium border border-black hover:bg-gray-200"
              onClick={onClose}
            >Close</button>
            {stepIndex === steps.length - 1 ? (
              <button
                className="px-3 py-1 rounded bg-black text-white text-xs font-medium border border-black hover:bg-gray-900"
                onClick={onClose}
              >Finish</button>
            ) : (
              <button
                className="px-3 py-1 rounded bg-black text-white text-xs font-medium border border-black hover:bg-gray-900"
                onClick={onNext}
              >Next</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour; 